import {Link} from 'react-router-dom';
import {useProducts} from '@common/billing/pricing-table/use-products';
import {findBestPrice} from '@common/billing/pricing-table/find-best-price';
import {useAuth} from '@common/auth/use-auth';
import {Product} from '@common/billing/product';
import {Price} from '@common/billing/price';
import {FormattedCurrency} from '@common/i18n/formatted-currency';
import {Trans} from '@common/i18n/trans';
import {setInLocalStorage} from '@common/utils/hooks/local-storage';
import {useSettings} from '@common/core/settings/use-settings';
import {ArrowRight, c, CheckMark} from './mrigl-shell';

const intervalLabel: Record<string, string> = {
  year: 'an',
  month: 'mois',
  week: 'semaine',
  day: 'jour',
};

interface MriglPacksProps {
  loader: 'landingPage' | 'pricingPage';
  variant?: 'compact' | 'full';
}

export function MriglPacks({loader, variant = 'compact'}: MriglPacksProps) {
  const query = useProducts(loader);

  if (!query.data) return <PackSkeleton variant={variant} />;

  const packs = query.data.products.filter(p => !p.hidden);
  if (packs.length === 0) return null;

  const cols =
    packs.length === 1
      ? 'md:grid-cols-1 md:max-w-480 md:mx-auto'
      : packs.length === 2
        ? 'md:grid-cols-2'
        : 'md:grid-cols-3';

  return (
    <div className={`grid grid-cols-1 gap-20 md:gap-24 ${cols}`}>
      {packs.map(p => (
        <PackCard key={p.id} product={p} variant={variant} />
      ))}
    </div>
  );
}

interface PackCardProps {
  product: Product;
  variant: 'compact' | 'full';
}

function PackCard({product, variant}: PackCardProps) {
  const {isLoggedIn, isSubscribed} = useAuth();
  const {
    billing: {flouci},
  } = useSettings();
  const price = findBestPrice('yearly', product.prices) ||
    findBestPrice('monthly', product.prices) ||
    product.prices[0];
  const shouldUseDirectFlouci = !!flouci?.enable;

  let upgradeRoute: string | undefined;
  if (!isLoggedIn) {
    upgradeRoute = '/register?redirectFrom=pricing';
  } else if (isSubscribed && !shouldUseDirectFlouci) {
    upgradeRoute = `/change-plan/${product.id}/${price?.id}/confirm`;
  } else if (!product.free && price) {
    upgradeRoute = shouldUseDirectFlouci
      ? `/checkout/${product.id}/${price.id}/flouci`
      : `/checkout/${product.id}/${price.id}`;
  } else if (product.free) {
    upgradeRoute = '/dashboard';
  }

  const featured = !!product.recommended;

  const ctaLabel = product.free
    ? 'Commencer'
    : featured
      ? 'Choisir'
      : 'Sélectionner';

  return (
    <article
      className={`pack-card rounded border bg-white ${
        featured ? 'pack-recommended' : ''
      }`}
      style={{
        borderColor: featured ? c.ink : c.rule,
        borderWidth: featured ? 2 : 1,
      }}
    >
      {featured && (
        <span className="pack-recommended-stripe" aria-label="Recommandé">
          Le plus populaire
        </span>
      )}

      <div className="pack-card-body">
        <div className="mb-20">
          <h3
            className="text-xl"
            style={{fontWeight: 700, color: c.ink, letterSpacing: 0}}
          >
            <Trans message={product.name} />
          </h3>
          {product.description && (
            <p
              className="mt-6 text-sm"
              style={{color: c.inkMuted, lineHeight: 1.5}}
            >
              <Trans message={product.description} />
            </p>
          )}
        </div>

        <PriceDisplay price={price} free={!!product.free} />

        <div className="mt-24">
          <Link
            to={upgradeRoute || '/register'}
            onClick={() => {
              if (isLoggedIn || product.free || !price) return;
              setInLocalStorage('be.onboarding.selected', {
                productId: product.id,
                priceId: price.id,
                gateway: shouldUseDirectFlouci ? 'flouci' : undefined,
              });
            }}
            className={
              featured
                ? 'btn-lime pack-cta inline-flex w-full items-center justify-center gap-8 rounded px-20 py-14 text-base font-medium'
                : 'pack-cta-dark inline-flex w-full items-center justify-center gap-8 rounded px-20 py-14 text-base font-medium'
            }
          >
            {ctaLabel}
            <ArrowRight />
          </Link>
        </div>

        {variant === 'full' && product.feature_list?.length > 0 && (
          <div
            className="pack-features mt-28 border-t pt-20"
            style={{borderColor: c.rule}}
          >
            <p
              className="mb-12 text-xs font-semibold uppercase"
              style={{color: c.inkMuted, letterSpacing: 0}}
            >
              Inclus
            </p>
            <ul className="m-0 list-none space-y-10 p-0">
              {product.feature_list.map(f => (
                <li
                  key={f}
                  className="flex items-start gap-10 text-sm"
                  style={{color: c.ink2, lineHeight: 1.5}}
                >
                  <span
                    className="mt-3 inline-flex h-16 w-16 shrink-0 items-center justify-center rounded-full"
                    style={{background: c.lime, color: c.ink}}
                    aria-hidden="true"
                  >
                    <CheckMark />
                  </span>
                  <span>
                    <Trans message={f} />
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </article>
  );
}

function PriceDisplay({price, free}: {price?: Price; free: boolean}) {
  if (free) {
    return (
      <div className="flex items-baseline gap-8">
        <span
          className="text-4xl"
          style={{
            fontWeight: 600,
            letterSpacing: 0,
            color: c.ink,
          }}
        >
          Gratuit
        </span>
      </div>
    );
  }

  if (!price) return null;

  const perInterval =
    intervalLabel[price.interval] ||
    `${price.interval_count} ${price.interval}`;

  return (
    <div className="flex items-baseline gap-8">
      <span
        className="text-5xl"
        style={{
          fontWeight: 600,
          letterSpacing: 0,
          color: c.ink,
          lineHeight: 1,
        }}
      >
        <FormattedCurrency
          value={price.amount / (price.interval_count ?? 1)}
          currency={price.currency}
          minimumFractionDigits={0}
        />
      </span>
      <span
        className="text-sm"
        style={{color: c.inkMuted, fontWeight: 500}}
      >
        / {perInterval}
      </span>
    </div>
  );
}

function PackSkeleton({variant}: {variant: 'compact' | 'full'}) {
  return (
    <div className="mx-auto grid max-w-480 grid-cols-1 gap-20 md:max-w-1280 md:grid-cols-3">
      {[0, 1, 2].map(i => (
        <div
          key={i}
          className="rounded border bg-white p-28"
          style={{borderColor: c.rule, minHeight: variant === 'full' ? 360 : 220}}
        >
          <div
            className="mb-12 h-16 w-96 rounded"
            style={{background: c.ruleSoft}}
          />
          <div
            className="mb-32 h-10 w-160 rounded"
            style={{background: c.ruleSoft}}
          />
          <div
            className="mb-24 h-40 w-128 rounded"
            style={{background: c.ruleSoft}}
          />
          <div
            className="h-44 w-full rounded"
            style={{background: c.ruleSoft}}
          />
        </div>
      ))}
    </div>
  );
}
